import { useState, type ChangeEvent, useEffect } from "react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Paperclip, Image, FilePlus2, MapPin, FileText, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
// Utils
import { cn } from "@/lib/utils";

type FileType = "image" | "document" | null;

interface FileUploadPopoverProps {
	initialFile: SelectedFile | null;
	onFileSelect: (data: {
		file: File;
		type: FileType;
		messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE";
		fileName?: string;
		fileUrl?: string;
		sendImmediately?: boolean;
		isClickReceive?: boolean;
	}) => void;
	isOpen?: boolean;
	setIsOpen?: (open: boolean) => void;
}


interface SelectedFile extends File {
	// Extend File type if needed
}

export function FileUploadPopover({ initialFile, onFileSelect, isOpen: controlledIsOpen,
	setIsOpen: setControlledIsOpen }: FileUploadPopoverProps) {
	const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(initialFile);
	const [fileType, setFileType] = useState<FileType>(null);
	const [acceptStatus, setAcceptStatus] = useState<"accept" | "no-accept">(
		"no-accept",
	);
	const [internalIsOpen, setInternalIsOpen] = useState<boolean>(false);

	// Use either controlled or internal state
	const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
	const setIsOpen = setControlledIsOpen || setInternalIsOpen;

	// Effects
	useEffect(() => {
		if (initialFile) {
			setSelectedFile(initialFile);
			setFileType(initialFile.type.startsWith('image/') ? 'image' : 'document');
			// Open the popover when initialFile changes from null to a file
			if (!selectedFile) {
				setIsOpen(true);
			}
		}
	}, [initialFile]);

	// Functions

	const handleFileSelect = (
		e: ChangeEvent<HTMLInputElement>,
		type: FileType,
	) => {
		const file = e.target.files?.[0];
		if (file) {
			setSelectedFile(file as SelectedFile);
			setFileType(type);
		}
	};

	const resetSelection = (): void => {
		setSelectedFile(null);
		setFileType(null);
		// setAcceptStatus("accept");
	};

	const handleSend = (): void => {
		if (selectedFile && fileType) {
			// Determine message type based on file type
			let messageType: "IMAGE" | "FILE" | "VIDEO" | "VOICE" = "FILE";

			if (fileType === "image") {
				messageType = "IMAGE";
			} else if (selectedFile.type.startsWith('video/')) {
				messageType = "VIDEO";
			} else if (selectedFile.type.startsWith('audio/')) {
				messageType = "VOICE";
			}

			console.log("FileUploadPopover - Sending file:", selectedFile.name, "type:", messageType);

			// Only include isClickReceive for document files (FILE type)
			const isClickReceiveValue = messageType === "FILE" ? acceptStatus === "accept" : undefined;

			onFileSelect({
				file: selectedFile,
				type: fileType,
				messageType,
				fileName: selectedFile.name,
				sendImmediately: true, // Add this flag to indicate immediate sending
				isClickReceive: isClickReceiveValue
			});
			resetSelection();
			setIsOpen(false);
		}
	};

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button size={"icon"} variant={"ghost"}>
					<Paperclip />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px]">
				{!selectedFile ? (
					// Original upload options
					<div className="grid grid-cols-2 gap-2 w-full h-full">
						<div className="flex relative flex-col gap-2 items-center p-2 rounded-lg shadow-md text-primary hover:bg-muted">
							<Image />
							<span>ຮູບພາບ</span>
							<input
								type="file"
								className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
								accept={"image/png, image/jpeg, image/jpg"}
								onChange={(e) => handleFileSelect(e, "image")}
							/>
						</div>
						<div className="flex relative flex-col gap-2 items-center p-2 rounded-lg shadow-md text-primary hover:bg-muted">
							<FilePlus2 />
							<span>ເອກະສານ</span>
							<input
								type="file"
								className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
								accept={
									"application/pdf, application/docx, application/doc, application/xls, application/xlsx, application/pptx"
								}
								onChange={(e) => handleFileSelect(e, "document")}
							/>
						</div>
						{/* <div className="flex relative flex-col gap-2 items-center p-2 rounded-lg shadow-md text-primary hover:bg-muted">
							<MapPin />
							<span>ຕຳແໜ່ງ</span>
						</div> */}
					</div>
				) : (
					// File preview and options
					<div className="space-y-4">
						<div className="flex justify-between items-center">
							<h4 className="font-medium">ພີວິວເອກະສານ</h4>
							<Button
								size="icon"

								className="w-6 h-6"
								onClick={resetSelection}
							>
								<X className="w-4 h-4" />
							</Button>
						</div>

						{/* File Preview */}
						<div className="p-4 rounded-lg border">
							{fileType === "image" ? (
								<div className="relative aspect-video">
									<img
										src={URL.createObjectURL(selectedFile)}
										alt="Preview"
										className="object-cover rounded"
									/>
								</div>
							) : (
								<div className="flex gap-2 items-center p-2 rounded bg-muted">
									<FileText className="w-4 h-4" />
									<span className="text-sm truncate">{selectedFile.name}</span>
								</div>
							)}
						</div>

						{/* Accept/No Accept Radio Options */}
						{/* {fileType === "document" && (
							<div className="flex justify-between items-center p-2 rounded-lg border">
								<Label htmlFor="require-acceptance">ຕ້ອງການການຕອບຮັບ</Label>
								<Switch
									id="require-acceptance"
									checked={acceptStatus === "accept"}
									onCheckedChange={(checked) =>
										setAcceptStatus(checked ? "accept" : "no-accept")
									}
								/>
							</div>
						)} */}

						{/* Send Button */}
						<Button className="w-full" onClick={handleSend}>
							ສົ່ງ
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
